import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Layouts } from 'components'
import { head } from 'helpers'
import * as api from 'api'
import s from './styles.module.scss'

const TOTAL_STEPS = 6

const STEPS = [
  { id: 1, label: 'Personal', icon: 'person', title: 'Personal Details', subtitle: 'Basic information about the candidate' },
  { id: 2, label: 'Physical', icon: 'straighten', title: 'Physical Attributes', subtitle: 'Height, weight and marital status' },
  { id: 3, label: 'Contact', icon: 'location_on', title: 'Contact Information', subtitle: 'Address and communication details' },
  { id: 4, label: 'Career', icon: 'school', title: 'Education & Career', subtitle: 'Qualification, occupation and income' },
  { id: 5, label: 'Family', icon: 'family_restroom', title: 'Family Details', subtitle: 'Parents and siblings' },
  { id: 6, label: 'Finish', icon: 'verified', title: 'References & Submit', subtitle: 'Source info, references, photo and declaration' },
]

const WEIGHT_OPTIONS = ['35kg to 45kg', '45kg to 55kg', '55kg to 65kg', '65kg to 75kg', '75kg and above']

const OCCUPATION_OPTIONS = [
  'Student',
  'Private Employment',
  'Public Sector/Government',
  'Business',
  'Self-employed/Freelancer',
  'Agriculture & Farming',
  'Social Work',
  'Politician',
  'Homemaker',
  'Unemployed',
  'Others',
]

const PARENT_OCCUPATION_OPTIONS = [
  'Private Employment',
  'Public Sector/Government',
  'Business',
  'Self-employed/Freelancer',
  'Agriculture & Farming',
  'Social Work',
  'Politician',
  'Homemaker',
  'Others',
]

const INITIAL_FORM = {
  fullName: '',
  dateOfBirth: '',
  gender: 'male',
  gotra: '',
  maritalStatus: '',
  heightFeet: '',
  heightInches: '',
  weight: '',
  customWeight: '',
  address: '',
  city: '',
  customCity: '',
  state: '',
  contactNumber: '',
  email: '',
  socialMediaHandle: '',
  qualification: '',
  stream: '',
  specialisation: '',
  occupation: '',
  familyStatus: '',
  fatherName: '',
  fatherOccupation: '',
  motherName: '',
  motherOccupation: '',
  siblingsBrothers: '0',
  siblingsSisters: '0',
  employerName: '',
  employerDesignation: '',
  bussinessorjobDescription: '',
  annualIncome: '',
  declaration: false,
  sourceType: 'admin_input',
  sourceNote: '',
  references: [
    { name: '', relation: '', phone: '', note: '' },
    { name: '', relation: '', phone: '', note: '' },
  ],
}

const Field = ({ label, required, children, fullWidth }) => (
  <div className={`${s.field}${fullWidth ? ` ${s.fieldFull}` : ''}`}>
    <label>
      {label}
      {required ? <span className={s.required}>*</span> : null}
    </label>
    {children}
  </div>
)

const ChipRadio = ({ name, value, label, checked, onChange }) => (
  <label className={`${s.chip}${checked ? ` ${s.chipActive}` : ''}`}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
    {label}
  </label>
)

export default function AdminMatrimonialCreate() {
  const [form, setForm] = useState(INITIAL_FORM)
  const [photo, setPhoto] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [qualifications, setQualifications] = useState([])
  const [qualificationField, setQualificationField] = useState({ type: '', options: [], placeholder: '' })
  const fileInputRef = useRef(null)

  const maxDob = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]
  const progressPercent = Math.round((currentStep / TOTAL_STEPS) * 100)
  const stepMeta = STEPS[currentStep - 1]

  useEffect(() => {
    head({ title: 'Create Matrimonial | BCN' })
  }, [])

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  useEffect(() => {
    ;(async () => {
      try {
        const st = await api.publicApi.locations.fetchAllStates({ state: '', limit: 100 })
        if (st.code === 200) setStates(st.payload.states || [])
      } catch (e) { /* noop */ }
      try {
        const q = await api.publicApi.qualification.getQualifications({})
        if (q.code === 200 || q.code === 'OK') {
          const arr = Array.isArray(q.payload)
            ? q.payload
            : Array.isArray(q.payload?.qualifications)
              ? q.payload.qualifications
              : []
          setQualifications(arr)
        }
      } catch (e) { /* noop */ }
    })()
  }, [])

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const onStateChange = async (value) => {
    setForm(prev => ({ ...prev, state: value, city: '', customCity: '' }))
    if (!value) {
      setCities([])
      return
    }
    try {
      const stateSelected = await api.publicApi.locations.fetchAllStates({ limit: 1, state: value })
      const fkStateId = stateSelected?.payload?.states?.[0]?._id
      if (!fkStateId) {
        setCities([])
        return
      }
      const cityRes = await api.publicApi.locations.fetchAllCitiesViaState({ limit: 500, city: '', fkStateId })
      if (cityRes.code === 200) setCities(cityRes.payload.cities || [])
    } catch (e) {
      setCities([])
    }
  }

  const updateRef = (idx, key, value) => {
    setForm(prev => {
      const refs = [...prev.references]
      refs[idx] = { ...refs[idx], [key]: value }
      return { ...prev, references: refs }
    })
  }

  const handlePhotoSelect = useCallback((file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      Alert.warn('Please upload an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      Alert.warn('File size must be under 5MB')
      return
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoPreview(URL.createObjectURL(file))
    setPhoto(file)
  }, [photoPreview])

  const validateStep = (step) => {
    if (step === 1) {
      if (!form.fullName.trim()) return 'Please enter full name'
      if (!form.dateOfBirth) return 'Please select date of birth'
      if (!form.gender) return 'Please select gender'
      if (!form.gotra.trim()) return 'Please enter gotra'
      if (!form.maritalStatus) return 'Please select marital status'
    }
    if (step === 3) {
      if (!form.address.trim()) return 'Please enter address'
      if (!form.state) return 'Please select state'
      if (!form.city) return 'Please select city'
      if (form.city === 'Other' && !form.customCity.trim()) return 'Please enter custom city'
      if (!/^\d{10}$/.test(form.contactNumber)) return 'Please enter a valid 10-digit contact number'
    }
    if (step === 4) {
      if (!form.qualification) return 'Please select qualification'
      if (!form.occupation) return 'Please select occupation'
      if (qualificationField.type === 'dropdown' && !form.specialisation) return 'Please select specialisation'
      if (qualificationField.type === 'text' && !form.specialisation?.trim()) return 'Please enter specialisation'
    }
    if (step === 6) {
      if (!form.references[0]?.name?.trim() || !form.references[1]?.name?.trim()) {
        return 'Both references must have a name'
      }
      if (!/^\d{10}$/.test(form.references[0]?.phone || '') || !/^\d{10}$/.test(form.references[1]?.phone || '')) {
        return 'Both references must have valid 10-digit phone numbers'
      }
      if (!form.declaration) return 'Please accept the declaration'
    }
    return null
  }

  const goToStep = (step) => {
    if (step < 1 || step > TOTAL_STEPS) return
    if (step > currentStep) {
      for (let i = currentStep; i < step; i += 1) {
        const error = validateStep(i)
        if (error) {
          Alert.warn(error)
          setCurrentStep(i)
          return
        }
      }
    }
    setCurrentStep(step)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNext = () => {
    const error = validateStep(currentStep)
    if (error) {
      Alert.warn(error)
      return
    }
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const resetForm = () => {
    setForm(INITIAL_FORM)
    setPhoto(null)
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoPreview(null)
    setCurrentStep(1)
    setQualificationField({ type: '', options: [], placeholder: '' })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const error = validateStep(6)
    if (error) {
      Alert.warn(error)
      return
    }

    setLoading(true)
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'references') {
          fd.append('references', JSON.stringify(v))
        } else if (k === 'declaration') {
          fd.append(k, v ? 'true' : 'false')
        } else if (v !== undefined && v !== null && v !== '') {
          fd.append(k, v)
        }
      })
      if (photo) fd.append('photo', photo)

      const res = await api.admin.matrimonial.createByAdmin(fd)
      if (res.code === 201) {
        Alert.success('Matrimonial profile created successfully')
        resetForm()
      } else {
        Alert.error(res.message || 'Failed to create profile')
      }
    } catch (err) {
      Alert.error(err?.message || 'Failed to create profile')
    } finally {
      setLoading(false)
    }
  }

  const showEmployerFields = form.occupation && !['Homemaker', 'Others', 'Unemployed', 'Student'].includes(form.occupation)
  const showJobDescription = ['Business', 'Private Employment', 'Public Sector/Government'].includes(form.occupation)

  return (
    <div className={s.page}>
      <Layouts.Classic title="Create Matrimonial Profile">
        <Link className={s.secondaryBtn} to="/admin/matrimonial/pendingReferences">
          <span className="material-icons-outlined">pending_actions</span>
          Pending References
        </Link>
      </Layouts.Classic>

      <div className={s.hero}>
        <p className={s.heroText}>
          Create a complete profile on behalf of a member. All fields are saved and the profile goes to pending approval.
        </p>

        <div className={s.progressWrap}>
          <div className={s.progressMeta}>
            <span>Step {currentStep} of {TOTAL_STEPS}</span>
            <span>{progressPercent}% complete</span>
          </div>
          <div className={s.progressBar}>
            <div className={s.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className={s.stepNav}>
          {STEPS.map(step => (
            <button
              key={step.id}
              type="button"
              className={`${s.stepPill}${currentStep === step.id ? ` ${s.stepPillActive}` : ''}${currentStep > step.id ? ` ${s.stepPillDone}` : ''}`}
              onClick={() => goToStep(step.id)}
            >
              <span className="material-icons-outlined">{step.icon}</span>
              {step.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={onSubmit} className={s.formCard}>
        <div className={s.sectionHeader}>
          <h2>{stepMeta.title}</h2>
          <p>{stepMeta.subtitle}</p>
        </div>

        {currentStep === 1 && (
          <div className={s.grid}>
            <Field label="Full Name" required fullWidth>
              <input value={form.fullName} onChange={e => setField('fullName', e.target.value)} placeholder="Candidate full name" />
            </Field>
            <Field label="Date of Birth" required>
              <input type="date" max={maxDob} value={form.dateOfBirth} onChange={e => setField('dateOfBirth', e.target.value)} />
            </Field>
            <Field label="Gotra" required>
              <input value={form.gotra} onChange={e => setField('gotra', e.target.value)} placeholder="Gotra" />
            </Field>
            <Field label="Gender" required fullWidth>
              <div className={s.chipRow}>
                <ChipRadio name="gender" value="male" label="Male" checked={form.gender === 'male'} onChange={e => setField('gender', e.target.value)} />
                <ChipRadio name="gender" value="female" label="Female" checked={form.gender === 'female'} onChange={e => setField('gender', e.target.value)} />
              </div>
            </Field>
            <Field label="Marital Status" required fullWidth>
              <div className={s.chipRow}>
                {['single', 'separated', 'divorced', 'widowed'].map(status => (
                  <ChipRadio
                    key={status}
                    name="maritalStatus"
                    value={status}
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                    checked={form.maritalStatus === status}
                    onChange={e => setField('maritalStatus', e.target.value)}
                  />
                ))}
              </div>
            </Field>
          </div>
        )}

        {currentStep === 2 && (
          <div className={s.grid}>
            <Field label="Height (Feet)">
              <select value={form.heightFeet} onChange={e => setField('heightFeet', e.target.value)}>
                <option value="">Select feet</option>
                {['3', '4', '5', '6', '7'].map(f => <option key={f} value={f}>{f} ft</option>)}
              </select>
            </Field>
            <Field label="Height (Inches)">
              <select value={form.heightInches} onChange={e => setField('heightInches', e.target.value)}>
                <option value="">Select inches</option>
                {Array.from({ length: 12 }, (_, i) => i.toString()).map(x => <option key={x} value={x}>{x} in</option>)}
              </select>
            </Field>
            <Field label="Weight">
              <select value={form.weight} onChange={e => setField('weight', e.target.value)}>
                <option value="">Select weight range</option>
                {WEIGHT_OPTIONS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </Field>
            {form.weight === '75kg and above' && (
              <Field label="Custom Weight">
                <input value={form.customWeight} onChange={e => setField('customWeight', e.target.value)} placeholder="e.g. 82kg" />
              </Field>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className={s.grid}>
            <Field label="Address" required fullWidth>
              <textarea rows={3} value={form.address} onChange={e => setField('address', e.target.value)} placeholder="Full address" />
            </Field>
            <Field label="State" required>
              <select value={form.state} onChange={e => onStateChange(e.target.value)}>
                <option value="">Select state</option>
                {states.slice().sort((a, b) => a.state.localeCompare(b.state)).map(st => (
                  <option key={st._id} value={st.state}>{st.state}</option>
                ))}
              </select>
            </Field>
            <Field label="City" required>
              <select value={form.city} onChange={e => setField('city', e.target.value)} disabled={!form.state}>
                <option value="">{form.state ? 'Select city' : 'Select state first'}</option>
                {cities.slice().sort((a, b) => a.city.localeCompare(b.city)).map(ct => (
                  <option key={ct._id} value={ct.city}>{ct.city}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </Field>
            {form.city === 'Other' && (
              <Field label="Custom City" required>
                <input value={form.customCity} onChange={e => setField('customCity', e.target.value)} placeholder="Enter city name" />
              </Field>
            )}
            <Field label="Contact Number" required>
              <input
                value={form.contactNumber}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10)
                  setField('contactNumber', val)
                }}
                placeholder="10-digit mobile number"
                inputMode="numeric"
              />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="email@example.com" />
            </Field>
            <Field label="Social Media Handle">
              <input value={form.socialMediaHandle} onChange={e => setField('socialMediaHandle', e.target.value)} placeholder="@username or profile link" />
            </Field>
          </div>
        )}

        {currentStep === 4 && (
          <div className={s.grid}>
            <Field label="Qualification" required>
              <select
                value={form.qualification}
                onChange={e => {
                  const value = e.target.value
                  setField('qualification', value)
                  setField('specialisation', '')
                  const sel = qualifications.find(q => q.qualification === value)
                  if (sel) setQualificationField({ type: sel.type || '', options: sel.options || [], placeholder: sel.placeholder || '' })
                  else setQualificationField({ type: '', options: [], placeholder: '' })
                }}
              >
                <option value="">Select qualification</option>
                {qualifications.map(q => <option key={q._id} value={q.qualification}>{q.qualification}</option>)}
              </select>
            </Field>
            <Field label="Stream">
              <input value={form.stream} onChange={e => setField('stream', e.target.value)} placeholder="Stream (optional)" />
            </Field>
            {qualificationField.type === 'dropdown' ? (
              <Field label="Specialisation" required>
                <select value={form.specialisation} onChange={e => setField('specialisation', e.target.value)}>
                  <option value="">Select specialisation</option>
                  {qualificationField.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </Field>
            ) : (
              <Field label="Specialisation" required={!!qualificationField.type}>
                <input
                  value={form.specialisation}
                  onChange={e => setField('specialisation', e.target.value)}
                  placeholder={qualificationField.placeholder || 'Specialisation'}
                />
              </Field>
            )}
            <Field label="Occupation" required fullWidth>
              <select value={form.occupation} onChange={e => setField('occupation', e.target.value)}>
                <option value="">Select occupation</option>
                {OCCUPATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </Field>
            <Field label="Annual Income">
              <input value={form.annualIncome} onChange={e => setField('annualIncome', e.target.value)} placeholder="e.g. 10 LPA" />
            </Field>
            {showEmployerFields && (
              <>
                <Field label="Employer Name">
                  <input value={form.employerName} onChange={e => setField('employerName', e.target.value)} />
                </Field>
                <Field label="Employer Designation">
                  <input value={form.employerDesignation} onChange={e => setField('employerDesignation', e.target.value)} />
                </Field>
              </>
            )}
            {showJobDescription && (
              <Field label="Business / Job Description" fullWidth>
                <textarea rows={3} value={form.bussinessorjobDescription} onChange={e => setField('bussinessorjobDescription', e.target.value)} />
              </Field>
            )}
          </div>
        )}

        {currentStep === 5 && (
          <div className={s.grid}>
            <Field label="Family Status">
              <input value={form.familyStatus} onChange={e => setField('familyStatus', e.target.value)} placeholder="e.g. Middle class" />
            </Field>
            <Field label="Father's Name">
              <input value={form.fatherName} onChange={e => setField('fatherName', e.target.value)} />
            </Field>
            <Field label="Father's Occupation">
              <select value={form.fatherOccupation} onChange={e => setField('fatherOccupation', e.target.value)}>
                <option value="">Select</option>
                {PARENT_OCCUPATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </Field>
            <Field label="Mother's Name">
              <input value={form.motherName} onChange={e => setField('motherName', e.target.value)} />
            </Field>
            <Field label="Mother's Occupation">
              <select value={form.motherOccupation} onChange={e => setField('motherOccupation', e.target.value)}>
                <option value="">Select</option>
                {PARENT_OCCUPATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </Field>
            <Field label="Brothers">
              <select value={form.siblingsBrothers} onChange={e => setField('siblingsBrothers', e.target.value)}>
                {Array.from({ length: 11 }, (_, i) => i.toString()).map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Sisters">
              <select value={form.siblingsSisters} onChange={e => setField('siblingsSisters', e.target.value)}>
                {Array.from({ length: 11 }, (_, i) => i.toString()).map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
          </div>
        )}

        {currentStep === 6 && (
          <>
            <div className={s.grid}>
              <Field label="Profile Source">
                <select value={form.sourceType} onChange={e => setField('sourceType', e.target.value)}>
                  <option value="admin_input">Admin Input</option>
                  <option value="family_reference">Family Reference</option>
                  <option value="open_source">Open Source</option>
                  <option value="self_submitted">Self Submitted</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Source Note" fullWidth>
                <input value={form.sourceNote} onChange={e => setField('sourceNote', e.target.value)} placeholder="How was this profile sourced?" />
              </Field>
            </div>

            <h3 className={s.subtitle}>References (2 required)</h3>
            {[0, 1].map(i => (
              <div key={i} className={s.refCard}>
                <h4>Reference {i + 1}</h4>
                <div className={s.grid}>
                  <Field label="Name" required>
                    <input value={form.references[i].name} onChange={e => updateRef(i, 'name', e.target.value)} />
                  </Field>
                  <Field label="Relation">
                    <input value={form.references[i].relation} onChange={e => updateRef(i, 'relation', e.target.value)} />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      value={form.references[i].phone}
                      onChange={e => updateRef(i, 'phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      inputMode="numeric"
                      placeholder="10-digit number"
                    />
                  </Field>
                  <Field label="Note" fullWidth>
                    <input value={form.references[i].note} onChange={e => updateRef(i, 'note', e.target.value)} />
                  </Field>
                </div>
              </div>
            ))}

            <div className={s.photoSection}>
              <Field label="Profile Photo">
                <div className={s.photoUpload}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className={s.photoPreview} />
                  ) : (
                    <div className={s.photoPlaceholder}>
                      <span className="material-icons-outlined">add_a_photo</span>
                      <span>Upload candidate photo</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={e => handlePhotoSelect(e.target.files?.[0] || null)}
                  />
                  <button type="button" className={s.secondaryBtn} onClick={() => fileInputRef.current?.click()}>
                    Choose Photo
                  </button>
                </div>
              </Field>
            </div>

            <label className={s.declaration}>
              <input type="checkbox" checked={form.declaration} onChange={e => setField('declaration', e.target.checked)} />
              <span>
                I confirm that the information provided is accurate to the best of my knowledge and this profile may be published after admin review.
              </span>
            </label>
          </>
        )}

        <div className={s.formActions}>
          {currentStep > 1 && (
            <button type="button" className={s.secondaryBtn} onClick={handlePrev}>
              <span className="material-icons-outlined">arrow_back</span>
              Back
            </button>
          )}
          {currentStep < TOTAL_STEPS ? (
            <button type="button" className={s.primaryBtn} onClick={handleNext}>
              Continue
              <span className="material-icons-outlined">arrow_forward</span>
            </button>
          ) : (
            <button type="submit" className={s.primaryBtn} disabled={loading}>
              {loading ? 'Creating Profile...' : 'Create Profile'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
